import { ArrowUpRight, Megaphone } from "lucide-react";
import { sponsors } from "@/data/sponsors";
import { SPONSORS } from "@/constants/testIds";
import { useSEO } from "@/lib/useSEO";
import { EVENTS, trackSponsor } from "@/lib/analytics";
import AdvertiseDialog from "@/components/AdvertiseDialog";

const PAGE_TITLE = "Sponsors | Parlay Assistant";
const PAGE_DESCRIPTION =
  "Discover trusted sports betting partners, communities, and tools recommended by Parlay Assistant.";

const SponsorCard = ({ sponsor }) => {
  const href = sponsor.url;

  const onCoverClick = () => {
    trackSponsor(EVENTS.SPONSOR_CARD_CLICKED, sponsor, "card");
  };
  const onLogoClick = (e) => {
    // Prevent the click from also triggering the underlying card cover anchor.
    e.stopPropagation();
    trackSponsor(EVENTS.SPONSOR_LOGO_CLICKED, sponsor, "logo");
  };
  const onVisitClick = (e) => {
    e.stopPropagation();
    trackSponsor(EVENTS.VISIT_SPONSOR_CLICKED, sponsor, "button");
  };

  return (
    <article
      data-testid={SPONSORS.card(sponsor.id)}
      className="pa-sponsor-card group"
    >
      {/* Full-card cover — fires sponsor_card_clicked for blank areas of the card */}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Visit ${sponsor.name}`}
        onClick={onCoverClick}
        data-testid={SPONSORS.cover(sponsor.id)}
        className="pa-sponsor-cover"
      >
        <span className="sr-only">Visit {sponsor.name}</span>
      </a>

      {/* Logo — separate anchor above the cover so its own event fires. */}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onLogoClick}
        aria-label={`${sponsor.name} — open sponsor site`}
        data-testid={SPONSORS.cardLogoLink(sponsor.id)}
        className="pa-sponsor-logo-wrap pa-sponsor-clickable"
      >
        <img
          src={sponsor.logo}
          alt={sponsor.alt || `${sponsor.name} Sponsor Logo`}
          loading="lazy"
          decoding="async"
          data-testid={SPONSORS.cardLogo(sponsor.id)}
          className="pa-sponsor-logo"
        />
      </a>

      <h3 className="font-display text-2xl md:text-[1.6rem] uppercase tracking-tight text-slate-900 text-center relative z-[2] pointer-events-none">
        {sponsor.name}
      </h3>

      {sponsor.description && (
        <p className="mt-2 text-slate-600 text-sm md:text-[0.95rem] leading-relaxed text-center relative z-[2] pointer-events-none">
          {sponsor.description}
        </p>
      )}

      <div className="mt-5 flex justify-center relative z-10">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onVisitClick}
          data-testid={SPONSORS.cardVisit(sponsor.id)}
          className="pa-btn pa-btn-primary !text-sm !px-6"
        >
          Visit Sponsor
          <ArrowUpRight size={16} aria-hidden="true" />
        </a>
      </div>
    </article>
  );
};

export const SponsorsPage = () => {
  useSEO({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: "/sponsors" });

  return (
    <main
      id="sponsors"
      data-testid={SPONSORS.page}
      className="max-w-6xl mx-auto px-5 md:px-8 mt-10 md:mt-14"
    >
      <div className="max-w-3xl">
        <p className="font-display text-emerald-400 text-xs tracking-[0.25em] uppercase">
          Featured Partners
        </p>
        <h1 className="mt-2 font-display text-white text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[1.02]">
          Our Sponsors
        </h1>
        <p className="mt-4 text-slate-300 text-base md:text-lg leading-relaxed">
          Parlay Assistant is proudly supported by trusted partners and communities that help
          sports bettors make smarter decisions. Check out our featured sponsors below.
        </p>
      </div>

      <div
        data-testid={SPONSORS.grid}
        className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
      >
        {sponsors.map((s) => (
          <SponsorCard key={s.id} sponsor={s} />
        ))}
      </div>

      <section
        aria-labelledby="advertise-heading"
        className="mt-12 md:mt-16 pa-card-dark p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5"
      >
        <div className="flex items-start gap-4 md:max-w-xl">
          <div
            aria-hidden="true"
            className="shrink-0 w-11 h-11 rounded-xl inline-flex items-center justify-center"
            style={{
              background: "rgba(16,185,129,0.14)",
              color: "#6ee7b7",
              border: "1px solid rgba(16,185,129,0.3)",
            }}
          >
            <Megaphone size={20} />
          </div>
          <div>
            <h2
              id="advertise-heading"
              className="font-display text-white text-xl md:text-2xl uppercase tracking-wide"
            >
              Advertise With Us
            </h2>
            <p className="mt-1.5 text-slate-300 text-sm md:text-base leading-relaxed">
              Reach thousands of sports bettors using Parlay Assistant every week. Sponsorship
              slots, affiliate placements, and custom integrations are available &mdash; get in
              touch to discuss a partnership that fits your brand.
            </p>
          </div>
        </div>
        <AdvertiseDialog triggerClassName="self-start md:self-auto" />
      </section>

      <p
        data-testid={SPONSORS.disclaimer}
        className="mt-10 md:mt-14 mb-4 text-slate-400 text-xs md:text-sm leading-relaxed max-w-4xl"
      >
        Sponsor links direct users to third-party websites and communities. Parlay Assistant does
        not own or control third-party content, products, services, or promotions. Please review
        each sponsor&rsquo;s terms and policies before participating.
      </p>
    </main>
  );
};

export default SponsorsPage;
